import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { fetchAllListRows } from 'src/api'
import useDropdowns from 'src/hooks/useDropdowns'
import { AddExcerciseMaster } from 'src/redux/actions'
import {
  buildSampleRows,
  CONTENT_PATH_DEPTH,
  importRows,
  parseStatus,
  requireValues,
  resolveContentPath,
  splitList,
} from 'src/util/bulkImport'
import { downloadCsv } from 'src/util/csv'

import BulkUploadPanel from '../helperComponents/BulkUploadPanel'

export const PRACTICE_LIST_CSV_HEADERS = [
  'language',
  'course',
  'chapter',
  'lesson',
  'name',
  'status',
  'practiceItems',
]

const SAMPLE_PRACTICE_LISTS = [
  {
    language: 'English',
    course: 'Basics of Greetings',
    chapter: 'Greetings Basics',
    lesson: 'Greeting Someone New',
    name: 'Greetings Practice Set',
    status: 'Active',
    practiceItems: 'Choose the Correct Image - Apple;Choose the Matching Audio - Greeting',
  },
  {
    language: 'English',
    course: 'Basics of Greetings',
    chapter: 'Greetings Basics',
    lesson: 'Saying Goodbye',
    name: 'Goodbye Practice Set',
    status: 'Active',
    practiceItems: '',
  },
  {
    language: 'English',
    course: 'Numbers and Counting',
    chapter: 'Numbers 1 to 20',
    lesson: 'Formal Introductions',
    name: 'Internal Review Draft',
    status: 'Inactive',
    practiceItems: 'Type What You Hear - Numbers;64f1a2b3c4d5e6f7a8b9c0d1',
  },
]

export const SAMPLE_PRACTICE_LIST_CSV_ROWS = buildSampleRows(
  PRACTICE_LIST_CSV_HEADERS,
  SAMPLE_PRACTICE_LISTS
)

export const downloadSamplePracticeListCsv = () =>
  downloadCsv('bulk-practice-lists-sample.csv', SAMPLE_PRACTICE_LIST_CSV_ROWS)

// The single form attaches practice items from a paginated picker; the CSV names
// them instead, so the whole catalogue is fetched once per upload and indexed by
// both name and id.
const loadPracticeItemIndex = async () => {
  const items = await fetchAllListRows('exerciseDetails')
  const byName = new Map()
  const byId = new Set()

  items.forEach(item => {
    if (item?._id) byId.add(item._id)
    const key = item?.name?.trim().toLowerCase()
    if (!key) return
    byName.set(key, (byName.get(key) || []).concat(item._id))
  })

  return { byName, byId }
}

const resolvePracticeItemIds = (value, index) =>
  splitList(value).map(entry => {
    if (index.byId.has(entry)) return entry

    const matches = index.byName.get(entry.toLowerCase()) || []
    if (!matches.length) throw new Error(`Practice item not found: "${entry}"`)
    if (matches.length > 1) {
      throw new Error(
        `${matches.length} practice items are named "${entry}" — use the practice item id instead`
      )
    }
    return matches[0]
  })

// Mirrors the payload AddEditExcerciseMaster posts.
export const buildPracticeListPayload = (row, { dropdowns, practiceItemIndex }) => {
  requireValues(row, ['name'])

  return {
    ...resolveContentPath(dropdowns, row, CONTENT_PATH_DEPTH.LESSON),
    name: row.name,
    isActive: parseStatus(row.status),
    exerciseDetails: resolvePracticeItemIds(row.practiceItems, practiceItemIndex),
  }
}

const BulkUploadPracticeList = ({ toggleDrawer, refetch }) => {
  const dispatch = useDispatch()
  const { dropdowns } = useDropdowns()

  const onImport = useCallback(
    async rows => {
      const practiceItemIndex = await loadPracticeItemIndex()
      return importRows(rows, async row => {
        const payload = buildPracticeListPayload(row, { dropdowns, practiceItemIndex })
        await dispatch(AddExcerciseMaster(payload, { rethrow: true }))
      })
    },
    [dispatch, dropdowns]
  )

  return (
    <BulkUploadPanel
      entityPlural="Practice Lists"
      headers={PRACTICE_LIST_CSV_HEADERS}
      onDownloadSample={downloadSamplePracticeListCsv}
      onImport={onImport}
      toggleDrawer={toggleDrawer}
      refetch={refetch}
      helpText={
        'Each row names its own language, course, chapter and lesson (name or id). ' +
        'practiceItems is optional and semicolon-separated — each entry is an existing Practice ' +
        'Item name or id, and items are attached in the order given.'
      }
    />
  )
}

export default BulkUploadPracticeList
